import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useSetRecoilState } from "recoil";
import { IoFlash } from "react-icons/io5";

import { useUser } from "MVVM/Hooks/useUser";
import { useTrialEligibility } from "MVVM/Hooks/useTrialEligibility";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { StripePaystationState } from "MVVM/State";
import { analytics } from "MVVM/Analytics";
import { TIER_STRIPE_LOOKUP } from "MVVM/Utilities/SubscriptionTiers";
import { EPriceTabView } from "MVVM/@types";
import type { ClipStatusTimeline } from "../../@types";
import { HAPPY_PATH_EVENTS } from "../../@types";

type SkipTheWaitCTAProps = {
  timeline: ClipStatusTimeline;
  activeIdx: number;
  isStale: boolean;
  isError: boolean;
};

// Upsell for FREE users stuck in the slow lane. Since pnd re-queues waiting
// clips into the faster capture queue on upgrade (pnd #291), upgrading also
// speeds up the clip they're watching, not just future ones. Only rendered
// during the wait states; hidden once recording starts.
export const SkipTheWaitCTA = ({
  timeline,
  activeIdx,
  isStale,
  isError,
}: SkipTheWaitCTAProps) => {
  const { allstarUser } = useUser();
  const { isTrialEligible, currentTier } = useTrialEligibility();
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const setStripePaystation = useSetRecoilState(StripePaystationState);

  const isFree = allstarUser?.user?.userGroup?.type === "FREE";
  const activeEvent = activeIdx >= 0 ? HAPPY_PATH_EVENTS[activeIdx] : undefined;
  const isWaiting =
    !isError &&
    (isStale || activeEvent === "SUBMITTED" || activeEvent === "QUEUED");
  const visible = showSubscriptionOverhaul && isFree && isWaiting;

  // Prop vocabulary matches "Upgrade Modal - *" (isTrial, currentTier) so the
  // trial funnel can be queried uniformly across surfaces; the dedicated event
  // name is the surface, same as "Dropdown Trial CTA Clicked". The direct
  // checkout modal fires nothing, so this click IS the funnel step — the
  // conversion side is measured in Stripe.
  const trackingProps = {
    trackId: timeline.trackId,
    game: timeline.game,
    contentType: timeline.contentType,
    queueName: timeline.queueName,
    clipState: activeEvent,
    stale: isStale,
    isTrial: isTrialEligible,
    currentTier,
  };

  const shownTrackIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!visible || shownTrackIds.current.has(timeline.trackId)) return;
    shownTrackIds.current.add(timeline.trackId);
    analytics.track("Clip Status Trial CTA Shown", {
      trackId: timeline.trackId,
      game: timeline.game,
      contentType: timeline.contentType,
      queueName: timeline.queueName,
      clipState: activeEvent,
      stale: isStale,
      isTrial: isTrialEligible,
      currentTier,
    });
  }, [visible, isTrialEligible, currentTier, activeEvent, isStale, timeline]);

  if (!visible) return null;

  // Trial-ineligible FREE users (already used their trial) see the same
  // button, but it opens the regular Unlimited checkout — the server fence
  // rejects repeat trials, so trial:true would dead-end them.
  const openCheckout = () => {
    analytics.track("Clip Status Trial CTA Clicked", trackingProps);
    setStripePaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      lookUp: TIER_STRIPE_LOOKUP.CREATOR_PLAT[EPriceTabView.Monthly],
      trial: isTrialEligible,
    });
  };

  return (
    <GlowWrap>
      <TrialButton
        onClick={openCheckout}
        title={isTrialEligible ? "Free for 7 days · No card needed" : undefined}
      >
        <IoFlash size={13} />
        {isTrialEligible ? "Skip the queue for free" : "Skip the queue"}
      </TrialButton>
    </GlowWrap>
  );
};

const glowSlide = keyframes`
  from { background-position: 0% 0; }
  to { background-position: 200% 0; }
`;

// Shared with KeepTheSpeedCTA, the trial-user sibling in the same card slot
export const GlowWrap = styled.div`
  animation: ${glowSlide} 3.5s linear infinite;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.tierPlatAlpha25},
    ${({ theme }) => theme.colors.tierPlat},
    ${({ theme }) => theme.colors.tierPlatAlpha25}
  );
  background-size: 200% 100%;
  border-radius: 9px;
  margin-top: 8px;
  padding: 1px;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const TrialButton = styled.button`
  align-items: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.tierPlat} 0%,
    ${({ theme }) => theme.colors.tierPlatDark} 100%
  );
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  justify-content: center;
  padding: 8px 12px;
  width: 100%;

  &:hover {
    filter: brightness(1.1);
  }
`;
